import { Analytics } from '@analytics';
import { generateBrandingFromSite } from '@api/company';
import { CircularProgress } from '@material-ui/core';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import messages from './messages';

// Card 102 (experimental) — "Generate from my site". The operator enters their
// website address; the backend scans it for colours, fonts, a logo and a
// headline and returns a brand draft, which the parent merges into the
// Branding form (and so into the live preview). Nothing is saved until the
// operator hits Save Branding, so a bad scan is a no-op.
const GenerateFromSiteBar = ({ companyId, onGenerated }) => {
  const intl = useIntl();
  const [url, setUrl] = useState('');
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');

  const handleGenerate = async e => {
    e.preventDefault();
    if (scanning || !url.trim()) return;
    setError('');
    setWarning('');
    setScanning(true);
    Analytics.track('Branding generated from site', { page: 'setup' });
    try {
      const result = await generateBrandingFromSite(companyId, url.trim());
      onGenerated((result && result.branding) || {});
      // Card 265 — the draft still lands (a heuristic, and nothing is saved
      // until Save Branding), but the operator is told it came from an error page.
      if (result && result.warning) setWarning(result.warning.message);
    } catch (err) {
      console.error('Generate branding from site failed', err);
      const serverMessage =
        err && err.response && err.response.data && err.response.data.error
          ? err.response.data.error.message
          : '';
      setError(serverMessage || intl.formatMessage(messages.generateFromSiteError));
    } finally {
      setScanning(false);
    }
  };

  return (
    <div className="rounded-xl border border-solid border-indigo-200 bg-indigo-50/60 p-4">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-slate-800">
          {intl.formatMessage(messages.generateFromSiteTitle)}
        </span>
        <span className="rounded-full border border-solid border-indigo-200 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-600">
          {intl.formatMessage(messages.experimental)}
        </span>
      </div>
      <p className="mt-1 mb-3 text-xs text-slate-500">
        {intl.formatMessage(messages.generateFromSiteHelp)}
      </p>
      {/* Stacked (not side-by-side): the Setup settings column can be very
          narrow next to the live preview, which would crush a row layout. */}
      <div className="flex flex-col gap-2">
        <input
          type="text"
          aria-label={intl.formatMessage(messages.generateFromSiteTitle)}
          className="w-full rounded-xl border border-solid border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100 transition-colors disabled:opacity-60"
          placeholder="www.yourcompany.com"
          value={url}
          disabled={scanning}
          onChange={e => setUrl(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleGenerate(e)}
        />
        <button
          type="button"
          onClick={handleGenerate}
          disabled={scanning || !url.trim()}
          className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-solid border-indigo-200 bg-white px-4 py-2.5 text-sm font-semibold text-indigo-700 transition-colors hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-60">
          {scanning ? (
            <>
              <CircularProgress size={14} />
              {intl.formatMessage(messages.scanningSite)}
            </>
          ) : (
            <>
              <span aria-hidden="true">✨</span>
              {intl.formatMessage(messages.generateFromSite)}
            </>
          )}
        </button>
      </div>
      {error && <div className="text-red-500 text-xs mt-2">{error}</div>}
      {warning && (
        <div role="alert" className="text-amber-700 text-xs mt-2">
          {warning}
        </div>
      )}
    </div>
  );
};

export default GenerateFromSiteBar;
